import { KeyboardEvent, useEffect, useMemo } from 'react';
import {
  ComparableListing,
  ComparableListings,
} from 'src/components/Listings/ComparableListings';
import { useActiveFocusContext } from 'src/contexts/ActiveFocusContext/ActiveFocusContext';
import { useAppContext } from 'src/contexts/AppContext';
import { Content } from 'src/contexts/ContentContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { InfoOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  getAllInPriceFromListPrice,
  getAllInPriceFromListPriceForListing,
} from 'src/utils/inventoryUtils';
import { getMarketplaceSettings } from 'src/utils/userUtils';
import {
  ActivityLogClient,
  CompListing,
  Listing,
  ListingPriceCalculation,
  Marketplace,
} from 'src/WebApiController';

import * as styles from './ConnectedComparableListings.css';

export type ListingWithSeatScore = Listing & {
  seatScore?: number | null | undefined;
};

export type CompListingWithSeatScore = CompListing & {
  seatScore?: number | null | undefined;
};

export type ConnectedComparableListingsProps = {
  isLoading?: boolean;
  currentListing?: ListingWithSeatScore;
  priceOverride?: number | null;
  currentListingFromListingGroup?: ListingWithSeatScore[];
  comparableListings: CompListingWithSeatScore[];
  seatScore?: number | null;
  sortBy?: 'price' | 'dealScore';
  showAutoPriceResults?: boolean;
  isExcludingFanInventory?: boolean;
  isSortDescending?: boolean;
  listingPriceCalc?: ListingPriceCalculation | null;
  /**
   * User's listings
   */
  listings?: Listing[] | null;
};

const mapListingToComparableListing = (
  listing: ListingWithSeatScore,
  isSelectedWithListingGroup: boolean,
  listingPriceCalc?: ListingPriceCalculation | null
): ComparableListing => {
  return {
    listingId: listing.id,
    section: listing.seating.section,
    row: listing.seating.row ?? undefined,
    seatFr: listing.seating.seatFr ?? undefined,
    seatTo: listing.seating.seatTo ?? undefined,
    price: getAllInPriceFromListPriceForListing(listing),
    unitCost: listing.unitCst?.amt ?? undefined,
    currencyCode: listing.currency,
    listingNotes: listing.seatTraits ?? undefined,
    // Price assuming same fees as seller’s account
    sellerNetProceeds: listing.listPrice,
    seatScore: listing.seatScore,
    isYourListing: true,
    availableTickets: listing.availQty,
    validPurchaseQuantities: listing.vldPurQty,
    isSelectedWithListingGroup: isSelectedWithListingGroup,
    autoPricingResultType: listingPriceCalc?.autoPricingResultType ?? undefined,
    autoPricingResultConstraintType:
      listingPriceCalc?.autoPricingConstraintType ?? undefined,
    shouldShowResultTypeAction: listing.isAutoPrc ?? false,
    officeId: listing.officeId,
  };
};

/**
 * @deprecated - Do not use. Cleanup as part of YourListingsInComps flag cleanup
 */
export function ConnectedComparableListings({
  isLoading,
  currentListing,
  currentListingFromListingGroup,
  comparableListings,
  listings,
  listingPriceCalc,
  isExcludingFanInventory,
  ...rest
}: ConnectedComparableListingsProps) {
  const { activeAccountWebClientConfig } = useAppContext();

  const { onKeyDownHandler, activeItem } = useActiveFocusContext();
  const activeItemIndex = activeItem?.index;

  const onCurrentKeyDownHandler = (e: KeyboardEvent) => {
    onKeyDownHandler(e, activeItemIndex ?? 0);
  };

  const marketplaceListingIdsSet = useMemo(() => {
    const flattenedListings = (listings?.flatMap(flattenListingGroup) ??
      []) as Listing[];

    return new Set<number>(
      flattenedListings.reduce((ids, listing) => {
        const stubHubMkpListings = listing.mkpListings?.find(
          ({ mkp }) => mkp === Marketplace.StubHub
        );
        if (stubHubMkpListings?.mkpListingId) {
          ids.push(parseInt(stubHubMkpListings?.mkpListingId));
        }
        return ids;
      }, [] as number[])
    );
  }, [listings]);

  const { loginContext } = useAppContext();
  const stubHubMkpSettings = getMarketplaceSettings(
    loginContext?.user?.activeAccount,
    Marketplace.StubHub,
    currentListing?.officeId
  );

  const currentListings = useMemo(() => {
    if (currentListingFromListingGroup) {
      return currentListingFromListingGroup.map((l) =>
        mapListingToComparableListing(l, true, listingPriceCalc)
      );
    }

    return currentListing
      ? [mapListingToComparableListing(currentListing, false, listingPriceCalc)]
      : undefined;
  }, [currentListing, currentListingFromListingGroup, listingPriceCalc]);

  const comparableListingsLoaded = useMemo(() => {
    return !isLoading && currentListing?.id != null;
  }, [currentListing?.id, isLoading]);

  useEffect(() => {
    if (comparableListingsLoaded && currentListing) {
      new ActivityLogClient(activeAccountWebClientConfig).trackListingCompsView(
        currentListing.id
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparableListingsLoaded]);

  const hasBetterPrice = useMemo(() => {
    const newPrcInclFanInv = listingPriceCalc?.newPrcInclFanInv;
    const newPrcExclFanInv = listingPriceCalc?.websitePrice?.amt;

    if (!newPrcInclFanInv || !newPrcExclFanInv) {
      return false;
    }
    return newPrcInclFanInv < newPrcExclFanInv;
  }, [listingPriceCalc]);

  if (isLoading) {
    return <PosSpinner />;
  }

  return (
    <Stack
      className={styles.root}
      direction="column"
      gap="l"
      onKeyDown={onCurrentKeyDownHandler}
    >
      {isExcludingFanInventory && hasBetterPrice && (
        <div className={styles.fanInventoryInfoContainer}>
          <InfoOutlineIcon
            fill={vars.color.textWarning}
            size={vars.iconSize.l}
          />
          <span className={styles.statementText}>
            <Content id={ContentId.NonCompetitiveListing} />
          </span>
        </div>
      )}
      <ComparableListings
        currentListings={currentListings}
        comparableListings={
          comparableListings
            ? comparableListings.map((compListing) => {
                const isAnchor = listingPriceCalc?.udrRefLstIds?.includes(
                  compListing.listingId
                );

                return {
                  listingId: compListing.listingId,
                  section: compListing.section ?? undefined,
                  row: compListing.row ?? undefined,
                  seatFr: compListing.seatFrom ?? undefined,
                  seatTo: compListing.seatTo ?? undefined,
                  price: compListing.sellerAllInPrice?.amt,
                  priceDisp: compListing.sellerAllInPrice?.disp,
                  currencyCode: compListing.currencyCode ?? undefined,
                  listingNotes: compListing.listingNotes ?? undefined,
                  sellerNetProceeds:
                    compListing.sellerNetProceeds?.amt ?? undefined,
                  isOutlier:
                    compListing.isWithoutOutlier == null
                      ? false
                      : compListing.isWithoutOutlier,
                  isAnchor: isAnchor,
                  isYourListing:
                    marketplaceListingIdsSet.has(compListing.listingId) ||
                    compListing.isSellerCompListing,
                  isFanInventory: Boolean(compListing.isFanInventory),
                  seatScore: compListing.seatScore,
                  availableTickets: compListing.availableTickets,
                  validPurchaseQuantities: compListing.validPurchaseQuantities,
                } as ComparableListing;
              })
            : []
        }
        floorPrice={
          (listingPriceCalc?.cmpFlr?.amt ?? currentListing?.procsFloor)
            ? getAllInPriceFromListPrice(
                listingPriceCalc?.cmpFlr?.amt ?? currentListing?.procsFloor,
                currentListing,
                stubHubMkpSettings?.sellerFee
              )
            : undefined
        }
        ceilingPrice={
          (listingPriceCalc?.cmpCeil?.amt ?? currentListing?.procsCeil)
            ? getAllInPriceFromListPrice(
                listingPriceCalc?.cmpCeil?.amt ?? currentListing?.procsCeil,
                currentListing,
                stubHubMkpSettings?.sellerFee
              )
            : undefined
        }
        listingPriceCalc={listingPriceCalc}
        isExcludingFanInventory={isExcludingFanInventory}
        {...rest}
      />
    </Stack>
  );
}
